import React from 'react'
import { EmployeeOnboardingContent } from './EmployeeOnboardingUtils'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import EmployeeInfoForm from './EmployeeInfoForm'
import palette from 'core-system/Themes/palette'
import Divider from 'core-system/Divider'
import OnboardingTasksDrawer from 'features/Onboarding/components/OnboardingTasksDrawer'
import Loading from 'core-system/Loading'
import styled from 'styled-components'
import PrimaryButton from 'core-system/Button/PrimaryButton'

const TermsFrameShell = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.5rem;
  overflow: hidden;
  background: ${palette.white};
`

const TermsFrame = styled.iframe`
  width: 100%;
  height: min(70vh, 46rem);
  border: none;
  display: block;
`

const TermsLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${palette.white};
`

const TermsActionsRow = styled(FlexContainer)`
  justify-content: flex-end;
  margin: 1rem 0 0.75rem;
`

interface EmployeeOnboardingViewProps {
  isOpen: boolean
  onClose: () => void
  loading: boolean
  updateEmployeeProfile: () => void
  profileDataLocal: any
  setProfileDataLocal: (profileDataLocal: any) => void
  disableSubmitButton: boolean
  stage: string
  finishOnboarding: () => void
}

const EmployeeOnboardingView = React.memo(
  ({
    isOpen,
    onClose,
    loading,
    updateEmployeeProfile,
    profileDataLocal,
    setProfileDataLocal,
    disableSubmitButton,
    stage,
    finishOnboarding,
  }: EmployeeOnboardingViewProps) => {
    if (stage === 'TERMS_AND_CONDITIONS') {
      const TERMS_URL = 'https://www.movewithfleet.com/mobility-card-terms'
      const [termsLoaded, setTermsLoaded] = React.useState(false)

      return (
        <>
          <OnboardingTasksDrawer isOpen={isOpen} onClose={onClose}>
            <div>
              <Text variant='h2'>
                {EmployeeOnboardingContent.acceptTerms.leftHeader}
              </Text>
              <Text
                variant='action3'
                textColor={palette.text.secondary}
                style={{ marginTop: '0.75rem' }}
              >
                Review the Fleet Mobility Card terms below. If the embedded view
                is hard to use, you can{' '}
                <a href={TERMS_URL} target='_blank' rel='noopener noreferrer'>
                  open the terms in a new tab
                </a>
                .
              </Text>
              <TermsActionsRow>
                <PrimaryButton
                  onClick={() => finishOnboarding()}
                  loading={loading}
                >
                  Accept
                </PrimaryButton>
              </TermsActionsRow>
              <Divider margin='0.75rem 0 1.25rem' />
              <TermsFrameShell>
                {!termsLoaded && (
                  <TermsLoadingOverlay aria-label='Loading terms'>
                    <Loading />
                  </TermsLoadingOverlay>
                )}
                <TermsFrame
                  title='Fleet Mobility Card Terms'
                  src={TERMS_URL}
                  onLoad={() => setTermsLoaded(true)}
                />
              </TermsFrameShell>
            </div>
          </OnboardingTasksDrawer>
        </>
      )
    }
    return (
      <OnboardingTasksDrawer
        isOpen={isOpen}
        onClose={onClose}
        primaryButton={{
          text: 'Submit',
          onClick: () => updateEmployeeProfile(),
          loading: loading,
          disabled: disableSubmitButton,
        }}
      >
        <FlexContainer flexDirection='column'>
          <Text variant='h2'>
            {EmployeeOnboardingContent.extraInfo.leftHeader}
          </Text>
          <Text
            variant='action3'
            textColor={palette.text.placeholder}
            style={{ marginTop: '1rem' }}
          >
            * indicates a required field
          </Text>
          <Divider margin='1.5rem 0' />
          <EmployeeInfoForm
            setHomeAddress={(homeAddress: string) =>
              setProfileDataLocal({
                ...profileDataLocal,
                homeAddress: homeAddress,
              })
            }
            unitNumber={profileDataLocal.unitNumber}
            setUnitNumber={(unitNumber: string) =>
              setProfileDataLocal({
                ...profileDataLocal,
                unitNumber: unitNumber,
              })
            }
            referredBy={profileDataLocal.referredBy}
            setReferredBy={(referredBy: string) =>
              setProfileDataLocal({
                ...profileDataLocal,
                referredBy: referredBy,
              })
            }
            referredColleagues={profileDataLocal.referredColleagues}
            setReferredColleagues={(referredColleagues: string[]) =>
              setProfileDataLocal({
                ...profileDataLocal,
                referredColleagues: referredColleagues,
              })
            }
            employeeCorpID={profileDataLocal.employeeCorpId}
            setEmployeeCorpID={(employeeCorpId: string) =>
              setProfileDataLocal({
                ...profileDataLocal,
                employeeCorpId: employeeCorpId,
              })
            }
            dob={profileDataLocal.dob}
            setDOB={(dob: any) =>
              setProfileDataLocal({ ...profileDataLocal, dob: dob })
            }
            commutePreference={profileDataLocal.currentTransport}
            setCommutePreference={(commutePreference: string) =>
              setProfileDataLocal({
                ...profileDataLocal,
                currentTransport: commutePreference.toUpperCase(),
              })
            }
          />
        </FlexContainer>
      </OnboardingTasksDrawer>
    )
  }
)

export default EmployeeOnboardingView
